"use client";

// Màn đăng nhập RIÊNG cho Sale Workspace (saleworkspace.vucar.vn).
// Dùng đúng cơ chế auth của E2E: POST tới dashboard (chủ auth) với cookie .vucar.vn
// chung, thành công thì reload để server gate render workspace. Chỉ khác giao diện —
// thương hiệu Sale Workspace, tối giản, mobile-first (là PWA cho sale).
// Nút "Đăng nhập với Google" giống màn /dang-nhap của dashboard.

import { useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { oauthErrorMessage, signInWithGoogle } from "@/lib/auth-client";

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ marginRight: 8 }}>
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
    </svg>
  );
}

function WorkspaceLoginInner() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [err, setErr] = useState("");
  // Dashboard OAuth callback redirect về ?error=<code> — map giống /dang-nhap.
  const oauthError = oauthErrorMessage(useSearchParams().get("error"));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading || ssoLoading) return;
    if (!email.trim() || !password) {
      setErr("Nhập email và mật khẩu.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      // Same-origin: proxy nội bộ gọi dashboard server-to-server rồi chuyển tiếp
      // cookie .vucar.vn về đây. KHÔNG fetch thẳng dashboard (cross-origin) nữa —
      // preflight bị CORS chặn nên throw "Lỗi kết nối" cho sale chưa có cookie.
      const res = await fetch("/api/workspace/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!res.ok) {
        setErr("Email hoặc mật khẩu chưa đúng.");
        setLoading(false);
        return;
      }
      // Cookie .vucar.vn đã set → reload để root layout re-eval và render workspace.
      window.location.href = "/";
    } catch {
      setErr("Lỗi kết nối. Vui lòng thử lại.");
      setLoading(false);
    }
  }

  async function onGoogle() {
    if (loading || ssoLoading) return;
    setSsoLoading(true);
    setErr("");
    try {
      await signInWithGoogle();
      // Thành công thì navigate sang Google — giữ trạng thái loading.
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Đăng nhập với Google thất bại.");
      setSsoLoading(false);
    }
  }

  const shownError = oauthError ?? (err || null);

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: "var(--background)",
        fontFamily: "var(--font-app-sans)",
        color: "var(--foreground)",
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          width: "100%",
          maxWidth: 380,
          background: "var(--card)",
          borderRadius: 16,
          padding: "30px 26px 26px",
          boxShadow: "var(--app-shadow-dialog)",
          border: "1px solid var(--border)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 4 }}>
          <span
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: "var(--primary)",
              color: "var(--primary-foreground)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 22,
              boxShadow: "var(--app-shadow-card)",
            }}
          >
            V
          </span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1 }}>Sale Workspace</div>
            <div style={{ fontSize: 12, color: "var(--muted-foreground)" }}>Vucar</div>
          </div>
        </div>

        <div style={{ fontSize: 13, color: "var(--secondary-foreground)", margin: "14px 0 18px" }}>
          Đăng nhập bằng tài khoản Vucar của bạn để vào workspace bán xe.
        </div>

        {shownError ? (
          <div style={{ color: "var(--destructive)", fontSize: 12.5, marginBottom: 12 }}>{shownError}</div>
        ) : null}

        <button
          type="button"
          onClick={onGoogle}
          disabled={loading || ssoLoading}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "11px 12px",
            borderRadius: 8,
            border: "1px solid var(--input)",
            background: "var(--card)",
            color: "var(--foreground)",
            fontWeight: 600,
            fontSize: 14.5,
            cursor: loading || ssoLoading ? "default" : "pointer",
            marginBottom: 14,
          }}
        >
          {ssoLoading ? "Đang chuyển tới Google…" : (<><GoogleMark />Đăng nhập với Google</>)}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "2px 0 14px" }}>
          <span style={{ height: 1, flex: 1, background: "var(--border)" }} />
          <span style={{ fontSize: 11, color: "var(--muted-foreground)", textTransform: "uppercase" }}>hoặc</span>
          <span style={{ height: 1, flex: 1, background: "var(--border)" }} />
        </div>

        <label style={{ fontSize: 12, fontWeight: 600, color: "var(--secondary-foreground)" }}>Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ten.ban@vucar.net"
          autoComplete="username"
          autoCapitalize="none"
          style={inputStyle}
        />

        <label style={{ fontSize: 12, fontWeight: 600, color: "var(--secondary-foreground)", marginTop: 12, display: "block" }}>
          Mật khẩu
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Mật khẩu"
          autoComplete="current-password"
          style={inputStyle}
        />

        <button
          type="submit"
          disabled={loading || ssoLoading}
          style={{
            width: "100%",
            marginTop: 18,
            padding: "12px",
            borderRadius: 8,
            border: "none",
            background: loading || ssoLoading ? "var(--muted-foreground)" : "var(--primary)",
            color: "var(--primary-foreground)",
            fontWeight: 700,
            fontSize: 15,
            cursor: loading || ssoLoading ? "default" : "pointer",
            transition: "background .15s",
          }}
        >
          {loading ? "Đang đăng nhập…" : "Đăng nhập"}
        </button>

        <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 14, textAlign: "center" }}>
          Cùng tài khoản bạn đăng nhập dashboard.vucar.vn
        </div>
      </form>
    </div>
  );
}

export function WorkspaceLogin() {
  return (
    <Suspense>
      <WorkspaceLoginInner />
    </Suspense>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  marginTop: 5,
  padding: "11px 12px",
  borderRadius: 8,
  border: "1px solid var(--input)",
  fontSize: 16, // ≥16px: tránh iOS auto-zoom khi focus
  outline: "none",
  boxSizing: "border-box",
  background: "var(--card)",
  color: "var(--foreground)",
};
